'use client';

import useFetchResource from '@lib/api/client/resources/useFetchResource';
import Endpoint from '@lib/api/endpoints';
import {
  FetchFrontendCarrierAndSaveHighwayCarrierDataByMcOrDotRequest,
  FrontendCarrier,
  USStates,
  usStateAbbreviationToName,
} from '@trucksmarter/shared';
import DallasBanner from '@ui/Dallas/Banner/DallasBanner';
import DallasButton from '@ui/Dallas/Buttons/DallasButton/DallasButton';
import DallasCard from '@ui/Dallas/Card/DallasCard';
import DallasForm from '@ui/Dallas/Forms/DallasForm';
import DallasNumberField from '@ui/Dallas/Forms/DallasNumberField/DallasNumberField';
import DallasStack from '@ui/Dallas/Stack/DallasStack';
import DallasText from '@ui/Dallas/Text/DallasText';
import DallasTextSkeleton from '@ui/Dallas/Text/DallasTextSkeleton';
import { EMPTY_VALUE_PLACEHOLDER } from '@ui/shared/text-placeholders';
import { useState } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import DallasSpinner from '../../Dallas/ProgressIndicators/Spinner/DallasSpinner';
import styles from './AuthorityIdentifierFormPageContent.module.scss';
import DallasLink from '../../Dallas/Link/DallasLink';

export enum AuthorityIdentifierCollectionSkipOption {
  All = 'all',
  DoesNotHaveAuthorityIdentifier = 'does_not_have_authority_identifier',
  CallToAction = 'call_to_action',
  None = 'none',
}

export enum AuthorityType {
  MC = 'MC',
  DOT = 'DOT',
}

export interface Authority {
  type: AuthorityType;
  identifier: string;
}

interface AuthorityFormValues {
  type: AuthorityType;
  authorityIdentifierNumber: number | null;
}

interface CommonProps {
  defaultValues: AuthorityFormValues;
  juristictionOverride?: USStates;
  onSubmit: (authority: Authority) => void | Promise<void>;
}

interface WithoutSkipProps {
  skipOption?: AuthorityIdentifierCollectionSkipOption.None;
  onSkip?: never;
}

interface WithSkipProps {
  skipOption: AuthorityIdentifierCollectionSkipOption;
  onSkip: () => void;
}

type Props = CommonProps & (WithoutSkipProps | WithSkipProps);

export default function AuthorityIdentifierFormPageContent({
  onSubmit,
  defaultValues,
  skipOption = AuthorityIdentifierCollectionSkipOption.None,
  juristictionOverride,
  onSkip,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authorityIdentifier, setAuthorityIdentifier] = useState<string | null>(
    defaultValues?.authorityIdentifierNumber?.toString() || null,
  );
  const form = useForm<AuthorityFormValues>({ defaultValues });
  const {
    watch,
    formState: { isValid, isDirty },
  } = form;
  const authorityType = watch('type');
  const alternativeAuthorityType =
    authorityType === AuthorityType.MC ? AuthorityType.DOT : AuthorityType.MC;
  const { data, error, isLoading } = useFetchResource<
    FetchFrontendCarrierAndSaveHighwayCarrierDataByMcOrDotRequest,
    FrontendCarrier
  >(
    Endpoint.FetchFrontendCarrierAndSaveHighwayCarrierDataByMcOrDot,
    authorityIdentifier != null
      ? authorityType === AuthorityType.MC
        ? { mcNumber: authorityIdentifier }
        : { dotNumber: authorityIdentifier }
      : null,
    {
      revalidateIfStale: false,
      shouldRetryOnError: false,
      bypassErrorSnackbar: true,
    },
  );
  const carrier = data
    ? {
        ...data,
        jurisdiction: juristictionOverride ?? data.jurisdiction,
      }
    : null;
  const carrierPlaceholder = (
    <DallasText variant="captionL" color="lightGray" align="center" as="p">
      {`Enter your ${authorityType} number and submit it. If you don't have a ${authorityType} number, change the authority type and use a ${alternativeAuthorityType} instead.`}
    </DallasText>
  );

  return (
    <DallasStack gap="2xl" alignHorizontal="center">
      <DallasText align="center" variant="headerRegularL">
        Enter your MC / DOT number
      </DallasText>
      <DallasForm
        form={form}
        onSubmit={(values) => {
          form.reset(values);
          setAuthorityIdentifier(
            values.authorityIdentifierNumber?.toString() || null,
          );
        }}
      >
        <DallasStack gap="lg" alignHorizontal="center">
          <DallasStack gap="lg" alignHorizontal="center">
            <DallasStack direction="row" gap="lg" alignVertical="end">
              <AuthorityTypeToggle name="type" />
              <DallasNumberField
                label={`${authorityType} Number`}
                name="authorityIdentifierNumber"
                errorPlacement="eyebrow-end"
                placeholder="1-8 Digits"
                minLength={1}
                maxLength={8}
                addOnEnd={
                  <DallasButton
                    label="Submit"
                    disabled={!isDirty || isLoading}
                    color="primary"
                    size="tiny"
                    submit
                    flex
                  />
                }
                required
              />
            </DallasStack>
            <div className={styles.searchResultWrapper}>
              {isDirty ? (
                carrierPlaceholder
              ) : isLoading ? (
                <CarrierInfoCardSkeleton />
              ) : error != null ? (
                <DallasStack gap="lg">
                  <DallasBanner text={error.displayMessage} color="error" />
                  <DallasText
                    variant="captionL"
                    color="lightGray"
                    align="center"
                  >
                    {`If you don't have a valid ${authorityType} number, change the authority type and use a ${alternativeAuthorityType} instead.`}
                  </DallasText>
                </DallasStack>
              ) : carrier != null ? (
                <CarrierInfoCard carrier={carrier} />
              ) : (
                carrierPlaceholder
              )}
            </div>
          </DallasStack>
          <DallasStack direction="row">
            <DallasButton
              label={isSubmitting ? '' : 'Continue'}
              addOnStart={isSubmitting ? <DallasSpinner /> : undefined}
              color="primary"
              disabled={
                !carrier ||
                !isValid ||
                isDirty ||
                isLoading ||
                isSubmitting ||
                authorityIdentifier == null
              }
              onClick={async () => {
                try {
                  setIsSubmitting(true);
                  await onSubmit({
                    type: authorityType,
                    identifier: authorityIdentifier!,
                  });
                } catch {
                  setIsSubmitting(false);
                }
              }}
              flex
            />
          </DallasStack>
          {skipOption !== AuthorityIdentifierCollectionSkipOption.None && (
            <DallasText>
              Don&apos;t have an MC or DOT?&nbsp;
              <DallasLink
                color="lightGray"
                onClick={() => !isLoading && !isSubmitting && onSkip?.()}
                underlined
              >
                Click here.
              </DallasLink>
            </DallasText>
          )}
        </DallasStack>
      </DallasForm>
    </DallasStack>
  );
}

const AUTHORITY_TYPE_OPTIONS = [AuthorityType.DOT, AuthorityType.MC] as const;

interface AuthorityTypeToggleProps {
  name: keyof AuthorityFormValues;
}

function AuthorityTypeToggle({ name }: AuthorityTypeToggleProps) {
  const { setValue, watch } = useFormContext<AuthorityFormValues>();
  const value = watch(name);
  return (
    <div className={styles.authorityTypeField}>
      <DallasText variant="captionL">Authority type</DallasText>
      <div className={styles.authorityTypeToggleButtonGroup}>
        {AUTHORITY_TYPE_OPTIONS.map((option) => (
          <DallasButton
            className={styles.authorityTypeToggleButton}
            key={option}
            label={option}
            onClick={() =>
              setValue(
                name,
                value === AuthorityType.MC
                  ? AuthorityType.DOT
                  : AuthorityType.MC,
              )
            }
            size="small"
            color={option === value ? 'primary' : 'tertiary'}
          />
        ))}
      </div>
    </div>
  );
}

interface CarrierInfoCardProps {
  carrier: FrontendCarrier;
}

function CarrierInfoCard({ carrier }: CarrierInfoCardProps) {
  return (
    <DallasCard
      className={styles.carrierInfoCard}
      gap="xs"
      padding="xs"
      content={
        <>
          <DallasText>{`${carrier.name}${
            carrier.dbaName ? ` dba ${carrier.dbaName}` : ''
          }`}</DallasText>
          <DallasStack gap="xl" direction="row">
            <DallasText>
              DOT#{carrier.dotNumber ?? EMPTY_VALUE_PLACEHOLDER}
            </DallasText>
            <DallasText>
              MC#{carrier.mcNumber ?? EMPTY_VALUE_PLACEHOLDER}
            </DallasText>
          </DallasStack>
          <DallasText>
            {carrier.jurisdiction != null &&
            carrier.jurisdiction in usStateAbbreviationToName
              ? usStateAbbreviationToName[carrier.jurisdiction as USStates]
              : EMPTY_VALUE_PLACEHOLDER}
          </DallasText>
        </>
      }
    />
  );
}

function CarrierInfoCardSkeleton() {
  return (
    <DallasCard
      className={styles.carrierInfoCardSkeleton}
      gap="xs"
      padding="xs"
      content={
        <>
          <DallasTextSkeleton fullLine />
          <DallasStack gap="xl" direction="row">
            <DallasTextSkeleton />
            <DallasTextSkeleton />
          </DallasStack>
          <DallasTextSkeleton partialLine />
        </>
      }
    />
  );
}
